import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import MonthView from "@/components/calendar/MonthView";
import WeekView from "@/components/calendar/WeekView";

const CalendarView = ({ slots = [], onSlotClick, onCreateSlot, mode = "view" }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month"); // month or week

  const goToPrevious = () => {
    if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const goToNext = () => {
    if (viewMode === "month") {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getWeekRange = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  };

  const { start: weekStart, end: weekEnd } = getWeekRange(currentDate);

  return (
    <div className="calendar-container">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            {viewMode === "month" 
              ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              : `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()} - ${monthNames[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${currentDate.getFullYear()}`
            }
          </h3>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View Mode Switcher */}
          <div className="flex gap-1 bg-slate-100 rounded-md p-1">
            <button
              data-testid="calendar-month-view-btn"
              onClick={() => setViewMode("month")}
              className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                viewMode === "month"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Month
            </button>
            <button
              data-testid="calendar-week-view-btn"
              onClick={() => setViewMode("week")}
              className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                viewMode === "week"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Week
            </button>
          </div>
          <Button
            data-testid="calendar-today-btn"
            onClick={goToToday}
            variant="outline"
            size="sm"
            className="border-slate-300 hover:bg-slate-50"
          >
            Today
          </Button>
          <Button
            data-testid="calendar-prev-btn"
            onClick={goToPrevious}
            variant="outline"
            size="sm"
            className="border-slate-300 hover:bg-slate-50"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            data-testid="calendar-next-btn"
            onClick={goToNext}
            variant="outline"
            size="sm"
            className="border-slate-300 hover:bg-slate-50"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === "month" ? (
        <MonthView 
          currentDate={currentDate}
          slots={slots}
          onSlotClick={onSlotClick}
          mode={mode}
        />
      ) : (
        <WeekView 
          currentDate={currentDate}
          slots={slots}
          onSlotClick={onSlotClick}
          onCreateSlot={onCreateSlot}
          mode={mode}
        />
      )}

      {/* Legend */}
      <div className="flex items-center gap-6 mt-6 px-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
          <span className="text-sm text-slate-600">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-rose-500"></div>
          <span className="text-sm text-slate-600">Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-sm text-slate-600">Pending</span>
        </div>
        {mode === "interpreter" && viewMode === "week" && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-100 border-2 border-blue-400 border-dashed"></div>
            <span className="text-sm text-slate-600">Drag to create availability</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;